/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { QPilotDiscountType } from "../../../core/models/qpilot/coupon.interface";

/**
 * Coupon Constants
 * Discount type options, dropdown values, and toast messages
 * Based on old QMC dashboard-coupons-create-edit.component.ts
 */

/**
 * Discount type dropdown options for the form
 * Maps API values to human-readable labels
 */
export interface DiscountTypeOption {
  value: string;
  label: string;
  description: string;
}

export const DISCOUNT_TYPE_OPTIONS: DiscountTypeOption[] = [
  {
    value: QPilotDiscountType.ReduceSubtotalByPercentage,
    label: "Reduce Subtotal By Percentage",
    description: "Reduce order subtotal by a percentage",
  },
  {
    value: QPilotDiscountType.ReduceSubtotalByAmount,
    label: "Reduce Subtotal By Amount",
    description: "Reduce order subtotal by a fixed amount",
  },
  {
    value: QPilotDiscountType.ReduceShippingByPercentage,
    label: "Reduce Shipping By Percentage",
    description: "Reduce shipping cost by a percentage",
  },
  {
    value: QPilotDiscountType.ReduceShippingByAmount,
    label: "Reduce Shipping By Amount",
    description: "Reduce shipping cost by a fixed amount",
  },
  {
    value: QPilotDiscountType.SetShippingToAmount,
    label: "Set Shipping To Amount",
    description: "Set shipping to a specific amount",
  },
  {
    value: QPilotDiscountType.ReduceProductPriceByPercentage,
    label: "Reduce Product Price By Percentage",
    description: "Reduce product price by a percentage",
  },
  {
    value: QPilotDiscountType.ReduceProductPriceByAmount,
    label: "Reduce Product Price By Amount",
    description: "Reduce product price by a fixed amount",
  },
];

/**
 * Discount type filter options for the list page
 * Includes "All" option for no filter
 */
export const DISCOUNT_TYPE_FILTER_OPTIONS = [
  { value: "", label: "All Types" },
  ...DISCOUNT_TYPE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  })),
];

/**
 * Toast messages for coupon operations
 */
export const COUPON_MESSAGES = {
  // Success messages
  CREATE_SUCCESS: "Coupon created successfully",
  UPDATE_SUCCESS: "Coupon updated successfully",
  DELETE_SUCCESS: "Coupon deleted successfully",
  EXPORT_SUCCESS: "Coupons exported successfully",
  IMPORT_SUCCESS: "Coupons imported successfully",

  // Error messages
  CREATE_ERROR: "Failed to create coupon",
  UPDATE_ERROR: "Failed to update coupon",
  DELETE_ERROR: "Failed to delete coupon",
  LOAD_ERROR: "Failed to load coupons",
  LOAD_SINGLE_ERROR: "Failed to load coupon details",
  EXPORT_ERROR: "Failed to export coupons",
  IMPORT_ERROR: "Failed to import coupons",
  VALIDATION_ERROR: "Please fix the validation errors before saving",
  CODE_DUPLICATE_ERROR: "A coupon with this code already exists",
  CODE_REQUIRED_ERROR: "Coupon code is required",
  PRODUCT_REQUIRED_ERROR: "Product is required for product discount types",
  PERCENTAGE_RANGE_ERROR: "Percentage must be between 0 and 100",

  // Confirmation messages
  DELETE_CONFIRM_TITLE: "Delete Coupon",
  DELETE_CONFIRM_MESSAGE: "Are you sure you want to delete this coupon?",
  DELETE_CONFIRM_ACCEPT: "Delete",
  DELETE_CONFIRM_REJECT: "Cancel",
} as const;

/**
 * Page size options for the coupons list
 */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Default page size
 */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Sortable columns in the coupons table
 */
export const SORTABLE_COLUMNS = [
  { field: "Id", label: "ID" },
  { field: "Code", label: "Code" },
  { field: "Name", label: "Name" },
  { field: "DiscountType", label: "Type" },
  { field: "Amount", label: "Amount" },
  { field: "ExpirationDate", label: "Expiration" },
  { field: "CreatedUtc", label: "Created" },
] as const;

/**
 * Default sort configuration
 */
export const DEFAULT_SORT = {
  field: "Id",
  order: "desc" as const,
};

/**
 * Coupon code validation
 * Matches old QMC couponKeyPress (dashboard-coupons-create-edit.component.ts:357-364)
 * Old QMC pattern: /^[a-zA-Z0-9_!@#$%&*+\-_]*$/
 * Allowed: letters (A-Z), numbers (0-9), and special characters (_ ! @ # $ % & * + -)
 */
export const COUPON_CODE_VALIDATION = {
  maxLength: 20,
  pattern: /^[A-Za-z0-9_!@#$%&*+\-]+$/,
  patternMessage:
    "Coupon code can only contain letters, numbers, and special characters (_ ! @ # $ % & * + -)",
};

/**
 * Coupon name validation
 */
export const COUPON_NAME_VALIDATION = {
  maxLength: 255,
};

/**
 * Get display label for a discount type
 */
export function getDiscountTypeLabel(discountType: string): string {
  const option = DISCOUNT_TYPE_OPTIONS.find(
    (opt) => opt.value === discountType,
  );
  return option?.label ?? discountType;
}

/**
 * Format discount amount based on type
 * Percentage types show as "X%", amount types show as "$X"
 */
export function formatDiscountAmount(
  discountType: string,
  amount: number,
  currencySymbol = "$",
): string {
  if (!amount && amount !== 0) return "-";

  const isPercentage =
    discountType === QPilotDiscountType.ReduceSubtotalByPercentage ||
    discountType === QPilotDiscountType.ReduceShippingByPercentage ||
    discountType === QPilotDiscountType.ReduceProductPriceByPercentage;

  if (isPercentage) {
    return `${amount}%`;
  }

  return `${currencySymbol}${amount.toFixed(2)}`;
}

/**
 * Check if discount type is percentage-based
 */
export function isPercentageDiscount(discountType: string): boolean {
  return (
    discountType === QPilotDiscountType.ReduceSubtotalByPercentage ||
    discountType === QPilotDiscountType.ReduceShippingByPercentage ||
    discountType === QPilotDiscountType.ReduceProductPriceByPercentage
  );
}

/**
 * Check if discount type is subtotal-based
 */
export function isSubtotalDiscount(discountType: string): boolean {
  return (
    discountType === QPilotDiscountType.ReduceSubtotalByPercentage ||
    discountType === QPilotDiscountType.ReduceSubtotalByAmount
  );
}

/**
 * Check if discount type is shipping-based
 */
export function isShippingDiscount(discountType: string): boolean {
  return (
    discountType === QPilotDiscountType.ReduceShippingByPercentage ||
    discountType === QPilotDiscountType.ReduceShippingByAmount ||
    discountType === QPilotDiscountType.SetShippingToAmount
  );
}
